const STATE_KEY = "hydrotools:hydrostack-swath:v1";
const SONAR_URL = "/api/hydrostack-fusion/v1/sonar/r2sonic/latest";
const MOTION_URL = "/api/hydrostack-fusion/v1/posmv/motion/latest";
const MAX_PINGS = 250;
const ROW_H = 2; // canvas pixels per ping row
const CANVAS_W = 640;
const CANVAS_H = 420;
const MOTION_WINDOW_MS = 60_000;

// ─── State ────────────────────────────────────────────────────────────────────

function readState() {
  const fallback = {
    autoRefresh: true,
    refreshMs: 50,
    colorMode: "depth",
    depthColorScale: "viridis",
    swathHalfWidthM: 75,
    rollStabilize: true,
    rollInvert: false,
    rollMountOffsetDeg: 0,
    showProfile: true,
    showWaterfall: false,
    showMotion: true,
    settingsOpen: false,
    motionMaxPoints: 800,
    dotSize: "large",
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const p = raw ? JSON.parse(raw) : {};
    if (!p || typeof p !== "object") return fallback;
    return {
      ...fallback,
      ...p,
      refreshMs: clampNum(Number(p.refreshMs), 50, 2000, fallback.refreshMs),
      swathHalfWidthM: clampNum(Number(p.swathHalfWidthM), 10, 1000, fallback.swathHalfWidthM),
      colorMode: ["depth", "beam", "intensity"].includes(String(p.colorMode || ""))
        ? String(p.colorMode) : fallback.colorMode,
      depthColorScale: COLORMAP_STOPS[String(p.depthColorScale || "")] ? String(p.depthColorScale) : fallback.depthColorScale,
      rollMountOffsetDeg: normalizeRollDeg(p.rollMountOffsetDeg ?? 0),
      motionMaxPoints: clampNum(Number(p.motionMaxPoints), 60, 2400, fallback.motionMaxPoints),
      autoRefresh: p.autoRefresh !== false,
      rollStabilize: p.rollStabilize !== false,
      rollInvert: p.rollInvert === true,
      showProfile: p.showProfile !== false,
      showWaterfall: p.showWaterfall === true,
      showMotion: p.showMotion !== false,
      dotSize: ["small","medium","large"].includes(String(p.dotSize || "")) ? String(p.dotSize) : fallback.dotSize,
      settingsOpen: p.settingsOpen === true,
    };
  } catch { return fallback; }
}

function writeState(s) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch {}
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function clampNum(v, lo, hi, fallback) {
  return Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : fallback;
}

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fmt(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function parseIsoMs(iso) {
  if (!iso) return NaN;
  const t = Date.parse(String(iso));
  return Number.isFinite(t) ? t : NaN;
}

function downsample(rows, maxN) {
  const src = Array.isArray(rows) ? rows : [];
  if (src.length <= maxN) return src;
  const out = [];
  for (let i = 0; i < maxN; i++) out.push(src[Math.round((i / (maxN - 1)) * (src.length - 1))]);
  return out;
}

async function requestJson(url, opts = {}) {
  const ms = Number(opts.timeoutMs || 8000);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  try {
    const resp = await fetch(url, {
      method: opts.method || "GET",
      signal: ctl.signal,
      headers: { "Content-Type": "application/json" },
      body: opts.body == null ? undefined : JSON.stringify(opts.body),
    });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(String(payload?.error || `HTTP ${resp.status}`));
    return payload;
  } finally { clearTimeout(t); }
}

// ─── Swath math (lifted from sonar-visuals) ──────────────────────────────────

function normalizeRollDeg(v) {
  let n = Number(v);
  if (!Number.isFinite(n)) return 0;
  while (n > 180) n -= 360;
  while (n < -180) n += 360;
  return n;
}

function buildEstimatedSwath(latest, count = 181) {
  const range = Number(latest?.rx_range);
  const bwDeg = Number(latest?.tx_beamwidth_vert_deg);
  if (!Number.isFinite(range) || range <= 0 || !Number.isFinite(bwDeg) || bwDeg <= 0) return [];
  const half = (bwDeg * Math.PI / 180) / 2;
  return Array.from({ length: count }, (_, i) => {
    const theta = -half + (i / (count - 1)) * 2 * half;
    return { across: range * Math.sin(theta), depth: range * Math.cos(theta), beamIndex: i, intensityRaw: null };
  });
}

function buildSwathPoints(latest) {
  const raw = Array.isArray(latest?.swath_points) ? latest.swath_points : [];
  if (raw.length) {
    return raw
      .map((p) => ({
        across: Number(p?.across_track_m),
        depth: Number(p?.depth_m),
        beamIndex: Number(p?.beam_index),
        slantRangeM: Number(p?.slant_range_m),
        beamAngleDeg: Number(p?.beam_angle_deg),
        intensityRaw: Number.isFinite(Number(p?.intensity_raw)) ? Number(p.intensity_raw) : null,
        detectionMethod: String(p?.detection_method || "").toLowerCase(),
      }))
      .filter((p) => Number.isFinite(p.across) && Number.isFinite(p.depth) && p.depth > 0);
  }
  return buildEstimatedSwath(latest);
}

function edgeDepthStats(points) {
  const rows = (Array.isArray(points) ? points : [])
    .filter((p) => Number.isFinite(p?.across) && Number.isFinite(p?.depth));
  if (rows.length < 8) return null;
  const sorted = rows.slice().sort((a, b) => a.across - b.across);
  const n = Math.max(3, Math.floor(sorted.length * 0.08));
  const lMean = sorted.slice(0, n).reduce((s, p) => s + p.depth, 0) / n;
  const rMean = sorted.slice(-n).reduce((s, p) => s + p.depth, 0) / n;
  return { lMean, rMean, delta: rMean - lMean };
}

function applyRollStab(points, rollDeg, invert = false) {
  const roll = Number(rollDeg);
  if (!Number.isFinite(roll) || !points.length) return points;
  const rad = (-(invert ? -roll : roll) * Math.PI) / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  return points.map((p) => {
    const a = Number(p.across), d = Number(p.depth);
    if (!Number.isFinite(a) || !Number.isFinite(d)) return p;
    return { ...p, across: a * c - d * s, depth: a * s + d * c };
  });
}

function chooseAutoInvert(points, rollDeg) {
  const roll = Number(rollDeg);
  if (!Number.isFinite(roll) || Math.abs(roll) < 0.05) return false;
  const dNormal = Math.abs(edgeDepthStats(applyRollStab(points, roll, false))?.delta ?? Infinity);
  const dInvert = Math.abs(edgeDepthStats(applyRollStab(points, roll, true))?.delta ?? Infinity);
  return dInvert < dNormal;
}

// ─── Canvas color helpers ─────────────────────────────────────────────────────

function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h, s, l) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const pp = 2 * l - q;
  return [
    Math.round(hue2rgb(pp, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(pp, q, h) * 255),
    Math.round(hue2rgb(pp, q, h - 1 / 3) * 255),
  ];
}

// t ∈ [0,1]: 0=shallow (bright cyan-blue), 1=deep (dark indigo)
function depthToRgb(t) { return hslToRgb((205 - 185 * t) / 360, 0.9, 0.52 + 0.10 * t); }

// t ∈ [0,1]: port (yellow) → nadir (blue) → stbd (yellow)  →  outer beam = warm, inner = cool
function beamToRgb(t) { return hslToRgb((30 + 190 * Math.abs(t - 0.5) * 2) / 360, 0.85, 0.55); }

// t ∈ [0,1]: black → white
function intensityToRgb(t) { const v = Math.round(255 * t); return [v, v, v]; }

// ─── Depth color scales (matches surfaces tool) ───────────────────────────────

const COLORMAPS = [
  { value: "blues_r",        label: "Blues – White"  },
  { value: "viridis",        label: "Viridis"        },
  { value: "turbo",          label: "Turbo"          },
  { value: "magma",          label: "Magma"          },
  { value: "rdylbu_r",       label: "Red–Blue"       },
  { value: "gray",           label: "Grayscale"      },
  { value: "pastel_rainbow", label: "Pastel Rainbow" },
  { value: "earth",          label: "Earth"          },
  { value: "polar",          label: "Polar"          },
  { value: "copper",         label: "Copper"         },
];

const COLORMAP_STOPS = {
  blues_r:        [[0,[8,48,107]],[0.4,[33,113,181]],[0.7,[189,215,231]],[1,[255,255,255]]],
  viridis:        [[0,[68,1,84]],[0.25,[59,82,139]],[0.5,[33,145,140]],[0.75,[94,201,98]],[1,[253,231,37]]],
  turbo:          [[0,[35,23,27]],[0.25,[68,118,247]],[0.5,[66,211,164]],[0.75,[212,239,7]],[1,[144,12,0]]],
  magma:          [[0,[0,0,4]],[0.25,[81,18,124]],[0.5,[190,60,80]],[0.75,[251,176,100]],[1,[252,253,191]]],
  rdylbu_r:       [[0,[49,54,149]],[0.25,[171,217,233]],[0.5,[255,255,191]],[0.75,[253,174,97]],[1,[165,0,38]]],
  gray:           [[0,[0,0,0]],[1,[255,255,255]]],
  pastel_rainbow: [[0,[180,120,200]],[0.17,[130,150,230]],[0.33,[100,200,200]],[0.5,[130,210,150]],[0.67,[220,220,120]],[0.83,[240,170,120]],[1,[230,140,150]]],
  earth:          [[0,[15,40,110]],[0.2,[25,100,150]],[0.38,[200,170,90]],[0.5,[100,160,80]],[0.7,[140,110,65]],[0.85,[170,155,135]],[1,[230,230,225]]],
  polar:          [[0,[30,60,180]],[0.25,[120,170,220]],[0.5,[255,255,255]],[0.75,[220,130,100]],[1,[180,30,30]]],
  copper:         [[0,[0,0,0]],[0.4,[85,35,5]],[0.75,[175,95,40]],[1,[255,175,85]]],
};

function colormapGradientCss(name) {
  const stops = COLORMAP_STOPS[name];
  if (!stops) return "#888";
  return `linear-gradient(to right,${stops.map(([t,[r,g,b]]) => `rgb(${r},${g},${b}) ${(t*100).toFixed(0)}%`).join(",")})`;
}

function getColormapColor(t, name) {
  const stops = COLORMAP_STOPS[name] || COLORMAP_STOPS.viridis;
  t = Math.max(0, Math.min(1, t));
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i][0] && t <= stops[i + 1][0]) { lo = stops[i]; hi = stops[i + 1]; break; }
  }
  const span = hi[0] - lo[0];
  const f = span < 1e-10 ? 0 : (t - lo[0]) / span;
  return [0, 1, 2].map(j => Math.round(lo[1][j] + f * (hi[1][j] - lo[1][j])));
}

// ─── Canvas waterfall ─────────────────────────────────────────────────────────

function paintWaterfall(canvas, pingHistory, opts = {}) {
  const W = canvas.width;
  const H = canvas.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // X-axis locked to rx_range (same as profile view) — falls back to swathHalfWidthM
  const rxRange = Number(opts.rxRange);
  const halfW = Number.isFinite(rxRange) && rxRange > 0 ? rxRange : Math.max(1, Number(opts.swathHalfWidthM) || 75);
  const axisMin = -halfW, axisMax = halfW;
  const colorMode = String(opts.colorMode || "depth");
  const depthColorScale = String(opts.depthColorScale || "blue");
  const toCol = (x) => (x - axisMin) / (axisMax - axisMin) * (W - 1);

  // Depth color range locked to 0→rxRange; beam/intensity still use dynamic global range
  const dMin = 0;
  const dMax = Number.isFinite(rxRange) && rxRange > 0 ? rxRange : 1;
  let iMin = Infinity, iMax = -Infinity;
  let bMin = Infinity, bMax = -Infinity;
  for (const ping of pingHistory) {
    for (const p of ping.points) {
      if (Number.isFinite(p.intensityRaw)) { if (p.intensityRaw < iMin) iMin = p.intensityRaw; if (p.intensityRaw > iMax) iMax = p.intensityRaw; }
      if (Number.isFinite(p.beamIndex)) { if (p.beamIndex < bMin) bMin = p.beamIndex; if (p.beamIndex > bMax) bMax = p.beamIndex; }
    }
  }
  if (!Number.isFinite(iMin)) iMin = 0;
  if (!Number.isFinite(iMax) || iMax <= iMin) iMax = iMin + 1;
  if (!Number.isFinite(bMin)) bMin = 0;
  if (!Number.isFinite(bMax) || bMax <= bMin) bMax = bMin + 1;
  const dRange = dMax - dMin, iRange = iMax - iMin, bRange = bMax - bMin;

  const imgData = ctx.createImageData(W, H);
  const px = imgData.data;

  // Background: dark navy
  for (let i = 0; i < px.length; i += 4) { px[i] = 10; px[i + 1] = 16; px[i + 2] = 33; px[i + 3] = 255; }

  // Faint nadir guide
  const nadirCol = Math.round(toCol(0));
  if (nadirCol >= 0 && nadirCol < W) {
    for (let row = 0; row < H; row++) {
      const off = (row * W + nadirCol) * 4;
      px[off] = 20; px[off + 1] = 60; px[off + 2] = 100; px[off + 3] = 255;
    }
  }

  // Draw pings: newest at top (row 0), oldest at bottom
  const maxRows = Math.floor(H / ROW_H);
  const slice = pingHistory.slice(-maxRows).reverse();

  for (let pi = 0; pi < slice.length; pi++) {
    const ping = slice[pi];
    const rowY = pi * ROW_H;
    if (rowY >= H) break;
    const rowEnd = Math.min(rowY + ROW_H, H);

    const sorted = ping.points
      .filter((p) => Number.isFinite(p.across) && Number.isFinite(p.depth))
      .sort((a, b) => a.across - b.across);
    if (!sorted.length) continue;

    for (let bi = 0; bi < sorted.length; bi++) {
      const cur = sorted[bi];
      const prev = bi > 0 ? sorted[bi - 1] : null;
      const next = bi < sorted.length - 1 ? sorted[bi + 1] : null;

      // Each beam "owns" its Voronoi midpoint span so there are no gaps or overlaps
      const beamHalfGap = next
        ? (next.across - cur.across) / 2
        : (prev ? (cur.across - prev.across) / 2 : 1);
      const leftEdge = prev ? (cur.across + prev.across) / 2 : cur.across - beamHalfGap;
      const rightEdge = next ? (cur.across + next.across) / 2 : cur.across + beamHalfGap;

      const colStart = Math.max(0, Math.floor(toCol(leftEdge)));
      const colEnd = Math.min(W, Math.ceil(toCol(rightEdge)) + 1);
      if (colStart >= colEnd) continue;

      let r, g, b;
      if (colorMode === "beam") {
        [r, g, b] = beamToRgb(Math.max(0, Math.min(1, (cur.beamIndex - bMin) / bRange)));
      } else if (colorMode === "intensity" && cur.intensityRaw != null) {
        [r, g, b] = intensityToRgb(Math.max(0, Math.min(1, (cur.intensityRaw - iMin) / iRange)));
      } else {
        [r, g, b] = getColormapColor(Math.max(0, Math.min(1, (cur.depth - dMin) / dRange)), depthColorScale);
      }

      for (let row = rowY; row < rowEnd; row++) {
        for (let col = colStart; col < colEnd; col++) {
          const off = (row * W + col) * 4;
          px[off] = r; px[off + 1] = g; px[off + 2] = b; px[off + 3] = 255;
        }
      }
    }
  }

  ctx.putImageData(imgData, 0, 0);

  // Axis labels overlay
  ctx.save();
  ctx.font = "10px monospace";
  ctx.fillStyle = "rgba(100,116,139,.85)";
  ctx.textAlign = "left";  ctx.fillText(`-${halfW}m`, 4, H - 5);
  ctx.textAlign = "right"; ctx.fillText(`+${halfW}m`, W - 4, H - 5);

  ctx.fillStyle = "rgba(125,211,252,.7)";
  ctx.textAlign = "center"; ctx.fillText("nadir", W / 2, H - 5);
  ctx.fillStyle = "rgba(100,116,139,.65)";
  ctx.textAlign = "right";  ctx.fillText(`${slice.length} pings`, W - 4, 13);
  ctx.restore();
}

// ─── Canvas: current-ping profile ─────────────────────────────────────────────

const PROFILE_W = 600, PROFILE_H = 320;

function paintProfileCanvas(canvas, points, rxRange, depthColorScale, dotSize) {
  const W = PROFILE_W, H = PROFILE_H;
  const pL = 38, pR = 14, pT = 10, pB = 22;
  const iW = W - pL - pR, iH = H - pT - pB;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = "rgba(10,16,33,0.95)";
  ctx.fillRect(0, 0, W, H);

  const rows = (Array.isArray(points) ? points : [])
    .filter((p) => Number.isFinite(p?.across) && Number.isFinite(p?.depth));

  if (!rows.length) {
    ctx.fillStyle = "#475569";
    ctx.font = "11px monospace";
    ctx.textAlign = "center";
    ctx.fillText("No swath data", W / 2, H / 2 + 4);
    return;
  }

  const depthVals = rows.map((p) => p.depth);
  const dMax = Math.max(...depthVals);
  const rangeM = Number.isFinite(rxRange) && rxRange > 0 ? rxRange : dMax * 1.15;
  const xMin = -rangeM, xMax = rangeM, yMin = 0, yMax = rangeM;
  const xFor = (x) => pL + (x - xMin) / Math.max(0.001, xMax - xMin) * iW;
  const yFor = (y) => pT + (y - yMin) / Math.max(0.001, yMax - yMin) * iH;
  const nadirX = xFor(0);

  // Axes
  ctx.strokeStyle = "rgba(148,163,184,0.25)";
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(pL, pT); ctx.lineTo(pL, H - pB);
  ctx.moveTo(pL, H - pB); ctx.lineTo(W - pR, H - pB);
  ctx.stroke();

  // Nadir line
  ctx.strokeStyle = "rgba(56,189,248,0.35)";
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(nadirX, pT); ctx.lineTo(nadirX, H - pB);
  ctx.stroke();
  ctx.setLineDash([]);

  // Sector wedge
  if (rows.length >= 4) {
    const sorted = rows.slice().sort((a, b) => a.across - b.across);
    const nEdge = Math.max(1, Math.floor(sorted.length * 0.05));
    const portPts = sorted.slice(0, nEdge);
    const stbdPts = sorted.slice(-nEdge);
    const portAngle = Math.atan2(portPts.reduce((s, p) => s + p.across, 0) / nEdge, portPts.reduce((s, p) => s + p.depth, 0) / nEdge);
    const stbdAngle = Math.atan2(stbdPts.reduce((s, p) => s + p.across, 0) / nEdge, stbdPts.reduce((s, p) => s + p.depth, 0) / nEdge);
    ctx.save();
    ctx.beginPath(); ctx.rect(pL, pT, iW, iH); ctx.clip();
    ctx.strokeStyle = "rgba(251,191,36,0.6)";
    ctx.lineWidth = 1.3;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(xFor(yMin * Math.tan(portAngle)), yFor(yMin));
    ctx.lineTo(xFor(yMax * Math.tan(portAngle)), yFor(yMax));
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(xFor(yMin * Math.tan(stbdAngle)), yFor(yMin));
    ctx.lineTo(xFor(yMax * Math.tan(stbdAngle)), yFor(yMax));
    ctx.stroke();
    ctx.restore();
    ctx.setLineDash([]);
    const clampX = (v) => Math.max(pL + 4, Math.min(W - pR - 4, v));
    ctx.fillStyle = "rgba(251,191,36,0.85)";
    ctx.font = "9px monospace";
    ctx.textAlign = "center";
    ctx.fillText((portAngle * 180 / Math.PI).toFixed(1) + "°", clampX(xFor(yMin * Math.tan(portAngle))), pT + 11);
    ctx.fillText("+" + (stbdAngle * 180 / Math.PI).toFixed(1) + "°", clampX(xFor(yMin * Math.tan(stbdAngle))), pT + 11);
  }

  // Dots — fillRect is much faster than arc() for many small points
  const dotPx = dotSize === "small" ? 1 : dotSize === "medium" ? 2 : 3;
  const half = dotPx / 2;
  for (const p of rows) {
    const [r, g, b] = getColormapColor(Math.max(0, Math.min(1, p.depth / rangeM)), depthColorScale);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(xFor(p.across) - half, yFor(p.depth) - half, dotPx, dotPx);
  }

  // Axis labels
  ctx.fillStyle = "#475569";
  ctx.font = "10px monospace";
  ctx.textAlign = "start";
  ctx.fillText(`Port (-${fmt(rangeM, 1)}m)`, pL, H - 6);
  ctx.textAlign = "end";
  ctx.fillText(`Stbd (+${fmt(rangeM, 1)}m)`, W - pR, H - 6);
  ctx.fillText(fmt(yMin, 1) + "m", pL - 5, pT + 10);
  ctx.fillText(fmt(dMax, 1) + "m", pL - 5, H - pB + 1);
}

// ─── SVG: motion strip ────────────────────────────────────────────────────────

function motionSvg(rows) {
  const W = 600, H = 120, pL = 36, pR = 10, pT = 10, pB = 20;
  const iW = W - pL - pR, iH = H - pT - pB;
  const samples = Array.isArray(rows) ? rows : [];
  if (!samples.length) {
    return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}"><rect width="${W}" height="${H}" rx="8" fill="rgba(10,16,33,.7)" stroke="rgba(148,163,184,.18)"/><text x="${W / 2}" y="${H / 2 + 4}" text-anchor="middle" fill="#475569" font-size="11">No motion data</text></svg>`;
  }

  const series = [
    { label: "Roll", color: "#fca5a5", vals: samples.map((r) => Number(r?.roll_deg)) },
    { label: "Pitch", color: "#86efac", vals: samples.map((r) => Number(r?.pitch_deg)) },
    { label: "Heave", color: "#22d3ee", vals: samples.map((r) => Number(r?.heave_m)) },
  ];

  const allVals = series.flatMap((s) => s.vals).filter(Number.isFinite);
  let yMin = allVals.length ? Math.min(...allVals) : -5;
  let yMax = allVals.length ? Math.max(...allVals) : 5;
  if (Math.abs(yMax - yMin) < 0.01) { yMin -= 1; yMax += 1; }
  const xFor = (i) => pL + (i / Math.max(1, samples.length - 1)) * iW;
  const yFor = (v) => pT + ((yMax - v) / (yMax - yMin)) * iH;
  const zeroY = yFor(0).toFixed(1);

  const paths = series.map((s) => {
    const pts = s.vals.map((v, i) => `${xFor(i).toFixed(1)},${(Number.isFinite(v) ? yFor(v) : yFor(0)).toFixed(1)}`).join(" ");
    return `<polyline points="${pts}" fill="none" stroke="${s.color}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/>`;
  }).join("");

  const legend = series.map((s, idx) => {
    const x = pL + idx * 100;
    return `<rect x="${x}" y="${pT}" width="10" height="2.5" rx="1.2" fill="${s.color}"/><text x="${x + 14}" y="${pT + 9}" fill="#cbd5e1" font-size="10">${s.label}</text>`;
  }).join("");

  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}">
    <rect width="${W}" height="${H}" rx="8" fill="rgba(10,16,33,.7)" stroke="rgba(148,163,184,.18)"/>
    <line x1="${pL}" y1="${pT}" x2="${pL}" y2="${H - pB}" stroke="rgba(148,163,184,.22)"/>
    <line x1="${pL}" y1="${H - pB}" x2="${W - pR}" y2="${H - pB}" stroke="rgba(148,163,184,.22)"/>
    <line x1="${pL}" y1="${zeroY}" x2="${W - pR}" y2="${zeroY}" stroke="rgba(148,163,184,.18)" stroke-dasharray="3 3"/>
    ${paths}
    ${legend}
    <text x="${pL - 5}" y="${pT + 9}" text-anchor="end" fill="#475569" font-size="10">${esc(fmt(yMax, 2))}</text>
    <text x="${pL - 5}" y="${H - pB + 1}" text-anchor="end" fill="#475569" font-size="10">${esc(fmt(yMin, 2))}</text>
    <text x="${pL}" y="${H - 5}" text-anchor="start" fill="#475569" font-size="10">-60s</text>
    <text x="${W - pR}" y="${H - 5}" text-anchor="end" fill="#475569" font-size="10">now</text>
  </svg>`;
}

// ─── Runtime ──────────────────────────────────────────────────────────────────

function createRuntime() {
  let el = null;
  let state = readState();
  let timer = null;
  let pingHistory = []; // [{points, pingNumber, nadirDepth, rollDeg, beamCount, ts}]
  let motionData = { timeline: [], latest: {} };
  let motionHistory = []; // accumulated client-side for motion strip
  let lastPingNumber = null;
  let lastFetchMs = 0;
  let fetchError = "";
  let pingTimestamps = []; // for rate calc
  let pendingRender = false;
  let pointerInside = false;
  let uiInteracting = false;
  let cmPickerOpen = false;
  let renderFrameId = null;
  let renderThrottleTimer = null;
  let lastRenderMs = 0;
  let canvasFrameId = null;
  let panelOpen = true;

  function persist() { writeState(state); }

  // ── Data fetch ─────────────────────────────────────────────────────────────

  async function poll() {
    const [sonarResult, motionResult] = await Promise.allSettled([
      requestJson(SONAR_URL),
      requestJson(MOTION_URL),
    ]);

    if (sonarResult.status === "fulfilled") {
      fetchError = "";
      lastFetchMs = Date.now();
      const latest = sonarResult.value?.latest || {};
      const pingNum = Number(latest?.ping_number);

      if (Number.isFinite(pingNum) && pingNum !== lastPingNumber) {
        lastPingNumber = pingNum;

        const rawPoints = buildSwathPoints(latest);
        const roll = Number(motionData?.latest?.roll_deg);
        const effectiveRoll = Number.isFinite(roll)
          ? roll + state.rollMountOffsetDeg
          : NaN;
        const invert = state.rollStabilize && Number.isFinite(effectiveRoll)
          ? (state.rollInvert ? !chooseAutoInvert(rawPoints, effectiveRoll) : chooseAutoInvert(rawPoints, effectiveRoll))
          : false;
        const points = state.rollStabilize && Number.isFinite(effectiveRoll)
          ? applyRollStab(rawPoints, effectiveRoll, invert)
          : rawPoints;

        // Nadir depth: average of beams within ±3m of track centre
        const nadirPts = points.filter((p) => Math.abs(p.across) <= 3);
        const nadirDepth = nadirPts.length
          ? nadirPts.reduce((s, p) => s + p.depth, 0) / nadirPts.length
          : (points.length ? points.reduce((a, b) => a.depth < b.depth ? a : b).depth : NaN);

        const rxRange = Number(latest?.rx_range);
        pingHistory.push({ points, pingNumber: pingNum, nadirDepth, rollDeg: effectiveRoll, beamCount: points.length, rxRange: Number.isFinite(rxRange) && rxRange > 0 ? rxRange : null, ts: Date.now() });
        if (pingHistory.length > MAX_PINGS) pingHistory.shift();

        const now = Date.now();
        pingTimestamps.push(now);
        pingTimestamps = pingTimestamps.filter((t) => now - t < 5000);
      }
    } else {
      fetchError = String(sonarResult.reason?.message || "Sonar unavailable");
    }

    if (motionResult.status === "fulfilled") {
      const latest = motionResult.value?.latest || {};
      if (Number.isFinite(Number(latest.roll_deg))) {
        const sample = { ...latest, received_at_utc: latest.received_at_utc || new Date().toISOString() };
        motionHistory.push(sample);
        const cutoff = Date.now() - MOTION_WINDOW_MS;
        motionHistory = motionHistory.filter(
          (m) => new Date(m.received_at_utc).getTime() > cutoff
        );
      }
      motionData = { latest, timeline: motionHistory };
    }

    scheduleRender();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function scheduleRender() {
    if (!panelOpen) { pendingRender = true; return; }
    if (uiInteracting || pointerInside) { pendingRender = true; return; }
    if (renderFrameId !== null || renderThrottleTimer !== null) return;
    const delay = 200 - (Date.now() - lastRenderMs); // ~5 Hz cap
    if (delay <= 0) {
      renderFrameId = requestAnimationFrame(() => {
        renderFrameId = null;
        lastRenderMs = Date.now();
        render();
      });
    } else {
      renderThrottleTimer = setTimeout(() => {
        renderThrottleTimer = null;
        renderFrameId = requestAnimationFrame(() => {
          renderFrameId = null;
          lastRenderMs = Date.now();
          render();
        });
      }, delay);
    }
  }

  function render() {
    if (!el || !panelOpen) return;
    pendingRender = false;

    const latestPing = pingHistory[pingHistory.length - 1] || null;
    const nadirDepth = latestPing?.nadirDepth;
    const beamCount = latestPing?.beamCount ?? 0;
    const pingRate = pingTimestamps.length > 1
      ? (pingTimestamps.length - 1) / ((pingTimestamps[pingTimestamps.length - 1] - pingTimestamps[0]) / 1000)
      : 0;
    const roll = Number(motionData?.latest?.roll_deg);
    const pitch = Number(motionData?.latest?.pitch_deg);
    const heave = Number(motionData?.latest?.heave_m);
    const ageMs = Date.now() - lastFetchMs;
    const isLive = lastFetchMs > 0 && ageMs < 3000;
    const statusDot = isLive ? "#22c55e" : "#ef4444";
    const statusGlow = isLive ? "box-shadow:0 0 7px #22c55e55;" : "";
    const statusLabel = isLive ? "LIVE" : (fetchError || "Connecting…");
    const statusColor = isLive ? "#86efac" : "#fca5a5";

    // Motion window: last 60s
    const motionRaw = Array.isArray(motionData?.timeline) ? motionData.timeline : [];
    const now = Date.now();
    let motionRows = motionRaw.filter((r) => {
      const t = parseIsoMs(r?.received_at_utc);
      return Number.isFinite(t) && now - t <= MOTION_WINDOW_MS;
    });
    if (!motionRows.length) motionRows = motionRaw;
    if (motionRows.length > state.motionMaxPoints) motionRows = downsample(motionRows, state.motionMaxPoints);

    el.innerHTML = `
<style>
  #hs-root { display:flex; flex-direction:column; gap:8px; }
  .hs-pill { background:rgba(15,23,42,.65); border:1px solid rgba(148,163,184,.14); border-radius:7px; padding:4px 10px; display:flex; flex-direction:column; align-items:center; min-width:72px; }
  .hs-pill-v { font-weight:700; font-size:14px; font-variant-numeric:tabular-nums; line-height:1.3; }
  .hs-pill-l { font-size:10px; color:#475569; }
  #hs-canvas { display:block; width:100%; border-radius:8px; background:rgba(10,16,33,.9); }
  .hs-section-label { font-size:11px; color:#475569; margin-bottom:3px; }
  .hs-settings-body { display:flex; flex-direction:column; gap:10px; padding:10px; background:rgba(10,16,33,.5); border-radius:8px; margin-top:6px; }
  .hs-settings-body label { font-size:12px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .hs-settings-body input[type=number], .hs-settings-body select { background:rgba(15,23,42,.8); border:1px solid rgba(148,163,184,.22); border-radius:5px; color:#e2e8f0; padding:3px 7px; font-size:12px; }
  .hs-settings-body input[type=number] { width:90px; }
</style>
<div id="hs-root">

  <!-- Status bar -->
  <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
    <div style="display:flex;align-items:center;gap:5px;">
      <div style="width:8px;height:8px;border-radius:50%;background:${statusDot};${statusGlow}flex-shrink:0;"></div>
      <span style="font-size:12px;color:${statusColor};">${esc(statusLabel)}</span>
    </div>
    <div class="hs-pill"><div class="hs-pill-v">${Number.isFinite(nadirDepth) ? esc(fmt(nadirDepth, 1)) + "m" : "—"}</div><div class="hs-pill-l">Nadir depth</div></div>
    <div class="hs-pill"><div class="hs-pill-v">${beamCount || "—"}</div><div class="hs-pill-l">Beams</div></div>
    <div class="hs-pill"><div class="hs-pill-v">${Number.isFinite(roll) ? esc(fmt(roll, 2)) + "°" : "—"}</div><div class="hs-pill-l">Roll</div></div>
    <div class="hs-pill"><div class="hs-pill-v">${Number.isFinite(pitch) ? esc(fmt(pitch, 2)) + "°" : "—"}</div><div class="hs-pill-l">Pitch</div></div>
    <div class="hs-pill"><div class="hs-pill-v">${Number.isFinite(heave) ? esc(fmt(heave, 2)) + "m" : "—"}</div><div class="hs-pill-l">Heave</div></div>
    <div class="hs-pill"><div class="hs-pill-v">${pingHistory.length > 1 ? esc(fmt(pingRate, 1)) + " Hz" : "—"}</div><div class="hs-pill-l">Ping rate</div></div>
  </div>

  <!-- Colormap picker + dot size -->
  <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
  ${(() => {
    const cmSel = COLORMAPS.find(c => c.value === state.depthColorScale) || COLORMAPS[1];
    const cmGrad = colormapGradientCss(state.depthColorScale);
    return `<div id="hs-cm-picker" style="position:relative;display:inline-block;">
      <button id="hs-cm-trigger" style="display:flex;align-items:center;gap:7px;padding:4px 8px;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:4px;cursor:pointer;font-size:11px;color:#e2e8f0;box-sizing:border-box;min-width:180px;">
        <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${cmGrad};flex-shrink:0;"></span>
        <span style="flex:1;text-align:left;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(cmSel.label)}</span>
        <span style="color:#64748b;font-size:9px;">▾</span>
      </button>
      ${cmPickerOpen ? `<div style="position:absolute;top:calc(100% + 2px);left:0;z-index:9999;background:rgba(2,6,23,0.97);border:1px solid rgba(148,163,184,0.2);border-radius:4px;overflow-y:auto;max-height:260px;box-shadow:0 8px 24px rgba(0,0,0,0.7);min-width:180px;">
        ${COLORMAPS.map(c => {
          const active = state.depthColorScale === c.value;
          return `<button data-hs-cm="${esc(c.value)}" style="display:flex;align-items:center;gap:8px;width:100%;padding:5px 8px;background:${active ? "rgba(56,189,248,0.1)" : "none"};border:none;border-bottom:1px solid rgba(148,163,184,0.06);cursor:pointer;font-size:11px;color:${active ? "#38bdf8" : "#e2e8f0"};text-align:left;box-sizing:border-box;">
            <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${colormapGradientCss(c.value)};flex-shrink:0;"></span>
            <span>${esc(c.label)}</span>
          </button>`;
        }).join("")}
      </div>` : ""}
    </div>`;
  })()}
  ${["small","medium","large"].map(sz => {
    const active = state.dotSize === sz;
    const label = sz === "small" ? "S" : sz === "medium" ? "M" : "L";
    return `<button data-hs-dot="${sz}" style="padding:3px 9px;font-size:11px;border-radius:4px;cursor:pointer;border:1px solid ${active ? "rgba(56,189,248,.6)" : "rgba(148,163,184,.2)"};background:${active ? "rgba(56,189,248,.15)" : "rgba(15,23,42,.5)"};color:${active ? "#38bdf8" : "#94a3b8"};">${label}</button>`;
  }).join("")}
  </div>

  <!-- Current ping profile -->
  ${state.showProfile ? `<div>
    <div class="hs-section-label">Current ping · ${esc(String(latestPing?.pingNumber ?? "—"))}</div>
    <canvas id="hs-profile" width="${PROFILE_W}" height="${PROFILE_H}" style="display:block;width:100%;border-radius:8px;"></canvas>
  </div>` : ""}

  <!-- Waterfall -->
  <div>
    <div class="hs-section-label" style="display:flex;align-items:center;justify-content:space-between;">
      <span>Waterfall${state.showWaterfall ? ` — newest at top · ${pingHistory.length} / ${MAX_PINGS} pings` : ""}</span>
      <button id="hs-wf-toggle" style="font-size:10px;padding:2px 8px;background:rgba(56,189,248,.12);border:1px solid rgba(56,189,248,.3);border-radius:4px;color:#38bdf8;cursor:pointer;">${state.showWaterfall ? "Hide" : "Show"}</button>
    </div>
    ${state.showWaterfall ? `<canvas id="hs-canvas" width="${CANVAS_W}" height="${CANVAS_H}"></canvas>` : ""}
  </div>

  <!-- Motion strip -->
  ${state.showMotion ? `<div>
    <div class="hs-section-label">Motion · last 60 s</div>
    ${motionSvg(motionRows)}
  </div>` : ""}

  <!-- Settings -->
  <details id="hs-settings-details" ${state.settingsOpen ? "open" : ""}>
    <summary style="font-size:12px;color:#64748b;cursor:pointer;user-select:none;">Settings</summary>
    <div class="hs-settings-body">
      <label>Color mode
        <select id="hs-color">
          <option value="depth" ${state.colorMode === "depth" ? "selected" : ""}>Depth</option>
          <option value="beam" ${state.colorMode === "beam" ? "selected" : ""}>Beam index</option>
          <option value="intensity" ${state.colorMode === "intensity" ? "selected" : ""}>Intensity</option>
        </select>
      </label>
      <label>Swath half-width (m)
        <input id="hs-hw" type="number" min="10" max="1000" step="5" value="${esc(String(state.swathHalfWidthM))}"/>
      </label>
      <label><input id="hs-roll-stab" type="checkbox" ${state.rollStabilize ? "checked" : ""}/> Roll stabilize</label>
      <label><input id="hs-roll-inv" type="checkbox" ${state.rollInvert ? "checked" : ""}/> Override auto-invert</label>
      <label>Mount roll offset (°)
        <input id="hs-roll-off" type="number" min="-180" max="180" step="0.1" value="${esc(String(state.rollMountOffsetDeg))}"/>
      </label>
      <label>Refresh (ms)
        <input id="hs-refresh" type="number" min="50" max="2000" step="10" value="${esc(String(state.refreshMs))}"/>
      </label>
      <label><input id="hs-show-profile" type="checkbox" ${state.showProfile ? "checked" : ""}/> Show ping profile</label>
      <label><input id="hs-show-wf" type="checkbox" ${state.showWaterfall ? "checked" : ""}/> Show waterfall</label>
      <label><input id="hs-show-motion" type="checkbox" ${state.showMotion ? "checked" : ""}/> Show motion strip</label>
      <button id="hs-clear" class="btn" style="align-self:flex-start;padding:4px 12px;font-size:12px;">Clear buffer</button>
    </div>
  </details>

</div>`;

    // Paint profile canvas (synchronous, after innerHTML)
    if (state.showProfile) {
      const profileCanvas = el.querySelector("#hs-profile");
      if (profileCanvas) {
        paintProfileCanvas(profileCanvas, latestPing?.points || [], latestPing?.rxRange ?? null, state.depthColorScale, state.dotSize);
      }
    }

    // Paint waterfall only when enabled (synchronous, after innerHTML)
    if (state.showWaterfall) {
      const canvas = el.querySelector("#hs-canvas");
      if (canvas) {
        if (pingHistory.length) {
          paintWaterfall(canvas, pingHistory, {
            swathHalfWidthM: state.swathHalfWidthM,
            colorMode: state.colorMode,
            depthColorScale: state.depthColorScale,
            rxRange: latestPing?.rxRange ?? null,
          });
        } else {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.fillStyle = "rgba(10,16,33,.9)";
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            ctx.fillStyle = "#475569";
            ctx.font = "13px monospace";
            ctx.textAlign = "center";
            ctx.fillText("Waiting for pings…", CANVAS_W / 2, CANVAS_H / 2);
          }
        }
      }
    }
  }

  // ── Controls ───────────────────────────────────────────────────────────────

  function pullForm() {
    if (!el) return;
    const g = (id) => el.querySelector(id);
    state.colorMode = String(g("#hs-color")?.value || state.colorMode);
    state.swathHalfWidthM = clampNum(Number(g("#hs-hw")?.value), 10, 1000, state.swathHalfWidthM);
    state.rollStabilize = g("#hs-roll-stab")?.checked !== false;
    state.rollInvert = g("#hs-roll-inv")?.checked === true;
    state.rollMountOffsetDeg = normalizeRollDeg(g("#hs-roll-off")?.value ?? state.rollMountOffsetDeg);
    state.refreshMs = clampNum(Number(g("#hs-refresh")?.value), 50, 2000, state.refreshMs);
    state.showProfile = g("#hs-show-profile")?.checked !== false;
    state.showWaterfall = g("#hs-show-wf")?.checked === true;
    state.showMotion = g("#hs-show-motion")?.checked !== false;
    persist();
  }

  let pollRunning = false;

  async function runPollLoop() {
    while (pollRunning) {
      await poll();
      if (!pollRunning) break;
      await new Promise((res) => { timer = setTimeout(res, state.refreshMs); });
    }
  }

  function startPolling() {
    stopPolling();
    if (!state.autoRefresh) return;
    pollRunning = true;
    runPollLoop();
  }

  function stopPolling() {
    pollRunning = false;
    if (timer) { clearTimeout(timer); timer = null; }
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  function onInput(e) {
    pullForm();
    stopPolling();
    startPolling();
  }

  function onClick(e) {
    // Dot size selection
    const dotBtn = e.target?.closest?.("[data-hs-dot]");
    if (dotBtn) {
      state.dotSize = dotBtn.dataset.hsDot;
      persist();
      render();
      return;
    }
    // Colormap item selection
    const cmBtn = e.target?.closest?.("[data-hs-cm]");
    if (cmBtn) {
      state.depthColorScale = cmBtn.dataset.hsCm;
      cmPickerOpen = false;
      persist();
      render();
      return;
    }
    // Colormap trigger toggle
    if (e.target?.closest?.("#hs-cm-trigger")) {
      cmPickerOpen = !cmPickerOpen;
      render();
      return;
    }
    // Close picker on outside click
    if (cmPickerOpen && !e.target?.closest?.("#hs-cm-picker")) {
      cmPickerOpen = false;
      render();
    }
    if (e.target?.closest?.("#hs-wf-toggle")) {
      state.showWaterfall = !state.showWaterfall;
      persist();
      render();
      return;
    }
    if (e.target?.closest?.("#hs-clear")) {
      pingHistory = [];
      pingTimestamps = [];
      lastPingNumber = null;
      render();
      return;
    }
    const details = e.target?.closest?.("#hs-settings-details");
    if (details) {
      setTimeout(() => { state.settingsOpen = !!el?.querySelector("#hs-settings-details[open]"); persist(); }, 0);
    }
  }

  function onPointerEnter() { pointerInside = true; }
  function onPointerLeave() { pointerInside = false; if (pendingRender) { pendingRender = false; render(); } }
  function onFocusIn(e) { if (e.target?.closest?.("input,select,button")) uiInteracting = true; }
  function onFocusOut() {
    if (!el) { uiInteracting = false; return; }
    const active = el.contains(document.activeElement) ? document.activeElement : null;
    uiInteracting = !!active?.closest?.("input,select,button");
    if (!uiInteracting && pendingRender) { pendingRender = false; render(); }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  function addListeners() {
    el.addEventListener("input", onInput);
    el.addEventListener("click", onClick);
    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointerleave", onPointerLeave);
    el.addEventListener("focusin", onFocusIn);
    el.addEventListener("focusout", onFocusOut);
  }

  function removeListeners() {
    el.removeEventListener("input", onInput);
    el.removeEventListener("click", onClick);
    el.removeEventListener("pointerenter", onPointerEnter);
    el.removeEventListener("pointerleave", onPointerLeave);
    el.removeEventListener("focusin", onFocusIn);
    el.removeEventListener("focusout", onFocusOut);
  }

  function mount(newEl) {
    if (el) removeListeners();
    el = newEl;
    addListeners();
    render();
    if (panelOpen) startPolling();
  }

  function destroy() {
    stopPolling();
    if (renderThrottleTimer !== null) { clearTimeout(renderThrottleTimer); renderThrottleTimer = null; }
    if (renderFrameId !== null) { cancelAnimationFrame(renderFrameId); renderFrameId = null; }
    if (canvasFrameId !== null) { cancelAnimationFrame(canvasFrameId); canvasFrameId = null; }
    if (el) { removeListeners(); el = null; }
  }

  function snapshot() { return { version: 1, state: { ...state } }; }

  function restore(data) {
    if (!data?.state || typeof data.state !== "object") return;
    const s = data.state;
    state = {
      ...state, ...s,
      refreshMs: clampNum(Number(s.refreshMs), 50, 2000, state.refreshMs),
      swathHalfWidthM: clampNum(Number(s.swathHalfWidthM), 10, 1000, state.swathHalfWidthM),
      rollMountOffsetDeg: normalizeRollDeg(s.rollMountOffsetDeg ?? state.rollMountOffsetDeg),
      motionMaxPoints: clampNum(Number(s.motionMaxPoints), 60, 2400, state.motionMaxPoints),
      autoRefresh: s.autoRefresh !== false,
      rollStabilize: s.rollStabilize !== false,
      rollInvert: s.rollInvert === true,
      showProfile: s.showProfile !== false,
      showMotion: s.showMotion !== false,
      settingsOpen: s.settingsOpen === true,
      depthColorScale: ["blue","viridis","plasma","inferno","rainbow","gray"].includes(String(s.depthColorScale || ""))
        ? String(s.depthColorScale) : state.depthColorScale,
    };
    persist();
    render();
    if (panelOpen) startPolling();
  }

  function setPanelOpen(open) {
    const next = !!open;
    if (panelOpen === next) return;
    panelOpen = next;
    if (panelOpen) {
      render();
      startPolling();
      if (pendingRender) { pendingRender = false; scheduleRender(); }
    } else {
      stopPolling();
      if (renderThrottleTimer !== null) { clearTimeout(renderThrottleTimer); renderThrottleTimer = null; }
      if (renderFrameId !== null) { cancelAnimationFrame(renderFrameId); renderFrameId = null; }
    }
  }

  return { mount, destroy, snapshot, restore, setPanelOpen };
}

// ─── Tool export ──────────────────────────────────────────────────────────────

export const hydrostackSwathTool = {
  id: "hydrostack-swath",
  name: "Swath Display",
  init() { this._rt = this._rt ?? createRuntime(); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(_app, el) {
    if (!this._rt) this._rt = createRuntime();
    this._rt.mount(el);
  },
  setPanelOpen(_app, open) { this._rt?.setPanelOpen?.(open); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(_app, data) {
    if (!this._rt) this._rt = createRuntime();
    this._rt.restore?.(data);
  },
};
